import {useParams, Link} from "react-router-dom";
import {useEffect, useState} from "react";
import ExamService from "../../service/exam.service";
import {ArrowUpDown, RotateCcw, FileText} from 'lucide-react';
import _ from 'lodash';
import {NotificationManager} from "react-notifications";

export default function ExamResults(props) {
    let {examId} = useParams();

    const [data, setData] = useState([]);
    const [schools, setSchools] = useState([]);
    const [selectedSchoolId, setSelectedSchoolId] = useState('');
    const [processedData, setProcessedData] = useState([]);

    const [duration, setDuration] = useState(25);

    const [searchFilters, setSearchFilters] = useState({
        studentId: '',
        name: '',
        school: '',
        grade: '',
        branch: '',
        status: ''
    });
    const [globalSearch, setGlobalSearch] = useState('');
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'asc'
    });
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 25;
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [resettingStudentId, setResettingStudentId] = useState(null);
    const [loading, setLoading] = useState(false);

    const effectiveExamId = props.examId || examId;

    useEffect(() => {

        let selectedExamId = effectiveExamId;
        if(!selectedExamId) return;

        setLoading(true);
        ExamService.getExamById(selectedExamId)
            .then((res) => {
                if (res) {
                    setDuration(Number(res.duration));

                    ExamService.getExamResults(selectedExamId)
                        .then((response) => {
                            if (response) {
                                try {
                                    // Map ApplicationExamSummaryDto to existing table shape
                                    const mapped = (Array.isArray(response) ? response : []).map((dto) => ({
                                        student: {
                                            studentId: dto.studentId,
                                            studentStudentId: dto.studentStudentId,
                                            studentName: dto.studentName,
                                            studentLastName: dto.studentLastName,
                                            name: dto.studentName,
                                            lastName: dto.studentLastName,
                                            school: dto.school,
                                            schoolId: dto.schoolId,
                                            grade: dto.grade,
                                            branch: dto.branch
                                        },
                                        examResult: { duration: dto.duration },
                                        assignmentId: dto.assignmentId,
                                        listeningAndPronunciation: dto.listeningAndPronunciation,
                                        textReadingAndSpeaking: dto.textReadingAndSpeaking,
                                        imageAnalysis: dto.imageAnalysis,
                                        videoAnalysis: dto.videoAnalysis,
                                        total: dto.total,
                                        duration: dto.duration,
                                        questionCount: dto.questionCount,
                                        answeredQuestionCount: dto.answeredQuestionCount,
                                        examDuration: dto.examDuration
                                    }));
                                    setData(mapped);
                                    const schoolsWithIds = mapped
                                        .filter(item => item.student != null)
                                        .map(item => ({
                                            school: item.student.school,
                                            schoolId: item.student.schoolId
                                        }));
                                    const uniqueSchools = _.uniqBy(schoolsWithIds, item => item.schoolId);
                                    setSchools(uniqueSchools);
                                } catch (error) {
                                    console.error('Error processing exam results:', error);
                                }
                            }
                            setLoading(false);
                        })
                        .catch((error) => {
                            console.error("Error fetching exam results:", error);
                            setData([]);
                            setSchools([]);
                            setLoading(false);
                        });
                } else {
                    setLoading(false);
                }
            })
            .catch((error) => {
                console.error("Error fetching exam:", error);
                setLoading(false);
            });



    }, [effectiveExamId, refreshTrigger]);

    const getStatusLabel = (item) => {
        const answered = item.answeredQuestionCount ?? 0;
        if (answered === 0) return "BAŞLAMADI";
        return "BİTTİ";
    };

    useEffect(() => {
        let filtered = [...data];
        if (selectedSchoolId) {
            filtered = filtered.filter(item => item.student?.schoolId === selectedSchoolId);
        }
        const safe = (v) => (v == null ? '' : String(v));
        const globalQuery = safe(globalSearch).toLowerCase();
        filtered = filtered.filter(item => {
            const student = item.student || {};
            const status = getStatusLabel(item);
            const studentIdDisplay = safe(student.studentStudentId || student.studentId);
            const fullName = `${safe(student.name)} ${safe(student.lastName)}`.trim();
            const matchesColumnFilters = (
                studentIdDisplay.toLowerCase().includes(safe(searchFilters.studentId).toLowerCase()) &&
                fullName.toLowerCase().includes(safe(searchFilters.name).toLowerCase()) &&
                safe(student.school).toLowerCase().includes(safe(searchFilters.school).toLowerCase()) &&
                safe(student.grade).toLowerCase().includes(safe(searchFilters.grade).toLowerCase()) &&
                safe(student.branch).toLowerCase().includes(safe(searchFilters.branch).toLowerCase()) &&
                status.toLowerCase().includes(safe(searchFilters.status).toLowerCase())
            );
            if (!globalQuery) {
                return matchesColumnFilters;
            }

            const globalHaystack = [
                studentIdDisplay,
                fullName,
                safe(student.school),
                safe(student.grade),
                safe(student.branch),
                status
            ].join(' ').toLowerCase();

            return matchesColumnFilters && globalHaystack.includes(globalQuery);
        });

        if (sortConfig.key) {
            const safeSort = (v) => (v == null ? '' : String(v));
            filtered = _.orderBy(
                filtered,
                [item => {
                    const student = item.student || {};
                    if (sortConfig.key === 'name') {
                        return `${safeSort(student.name)} ${safeSort(student.lastName)}`.trim().toLowerCase();
                    }
                    if (sortConfig.key === 'status') {
                        return getStatusLabel(item);
                    }
                    if (sortConfig.key === 'studentId') {
                        return safeSort(student.studentStudentId || student.studentId).toLowerCase();
                    }
                    return safeSort(student[sortConfig.key]).toLowerCase();
                }],
                [sortConfig.direction]
            );
        }
        setProcessedData(filtered);
    }, [data, selectedSchoolId, searchFilters, sortConfig, globalSearch]);

    const handleSort = (key) => {
        setSortConfig({
            key,
            direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
        });
    };

    const handleSearchChange = (key, value) => {
        setSearchFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setCurrentPage(1);
    };

    const handleResetStudentData = async (studentId) => {
        if (!effectiveExamId) return;
        if (!window.confirm(`"${studentId}" öğrencisinin bu sınava ait tüm cevap kayıtları silinecek. Devam etmek istiyor musunuz?`)) return;
        setResettingStudentId(studentId);
        try {
            const deletedCount = await ExamService.deleteStudentAnswerRecords(studentId, effectiveExamId);
            if (deletedCount != null) {
                NotificationManager.success(`${deletedCount} cevap kaydı silindi.`, 'Başarılı', 3000);
                setRefreshTrigger(t => t + 1);
            }
        } finally {
            setResettingStudentId(null);
        }
    };

    const pageCount = Math.ceil(processedData.length / itemsPerPage);
    const paginatedData = processedData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const formatScore = (v) => {
        if (v == null || typeof v !== 'number') return '—';
        return Number.isInteger(v) ? String(v) : v.toFixed(1);
    };

    const getStatusClasses = (status) => {
        if (status === "BAŞLAMADI") {
            return "bg-gray-100 text-gray-700 ring-1 ring-gray-200";
        }
        if (status === "BİTTİ") {
            return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200";
        }
        return "bg-amber-50 text-amber-700 ring-1 ring-amber-200";
    };

    return (
        <div className="p-4 space-y-3">
            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-lg-between gap-2">
                <div className="small text-muted">
                    Toplam <span className="fw-semibold text-dark">{processedData.length}</span> kayıt bulundu
                </div>
                <div className="d-flex gap-2 align-items-center">
                    <span className="small text-muted">Okul filtresi:</span>
                    <select
                        value={selectedSchoolId}
                        onChange={(e) => {
                            setSelectedSchoolId(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="form-select form-select-sm"
                        style={{minWidth: 220}}
                    >
                        <option value="">Tüm Okullar</option>
                        {schools.map((school, index) => (
                            <option key={index} value={school.schoolId}>
                                {school.school} (ID: {school.schoolId})
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="d-flex align-items-center gap-2">
                <div className="flex-grow-1">
                    <input
                        type="text"
                        value={globalSearch}
                        onChange={(e) => {
                            setGlobalSearch(e.target.value);
                            setCurrentPage(1);
                        }}
                        placeholder="Genel arama (isim, okul, ID, durum...)"
                        className="form-control form-control-sm"
                    />
                </div>
                <div className="d-flex align-items-center gap-2">
                    <span className="small text-muted d-none d-sm-inline">Sıralama:</span>
                    <select
                        value={sortConfig.key || ''}
                        onChange={(e) => {
                            const key = e.target.value || null;
                            setSortConfig(prev => ({
                                ...prev,
                                key
                            }));
                            setCurrentPage(1);
                        }}
                        className="form-select form-select-sm"
                    >
                        <option value="">Varsayılan</option>
                        <option value="studentId">Öğrenci ID</option>
                        <option value="name">Adı Soyadı</option>
                        <option value="school">Okul</option>
                        <option value="grade">Sınıf</option>
                        <option value="branch">Şube</option>
                        <option value="status">Durum</option>
                    </select>
                </div>
            </div>

            <div className="table-responsive mt-2 position-relative min-vh-50">
                {loading ? (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                        <div className="spinner-border text-primary" role="status" aria-label="Yükleniyor">
                            <span className="visually-hidden">Yükleniyor</span>
                        </div>
                        <span className="mt-2">Yükleniyor...</span>
                    </div>
                ) : (
                <table className="table table-striped table-hover align-middle mb-0">
                    <thead className="table-light small text-muted text-uppercase">
                    <tr>
                        <th scope="col" className="text-nowrap">Detay</th>
                        <th scope="col">Öğrenci ID</th>
                        <th scope="col">
                            <button
                                onClick={() => handleSort('name')}
                                className="btn btn-link p-0 text-decoration-none text-muted text-start small"
                            >
                                Adı Soyadı
                                <ArrowUpDown size={14} className="ms-1"/>
                            </button>
                        </th>
                        <th scope="col">
                            <button
                                onClick={() => handleSort('school')}
                                className="btn btn-link p-0 text-decoration-none text-muted text-start small"
                            >
                                Okul
                                <ArrowUpDown size={14} className="ms-1"/>
                            </button>
                        </th>
                        <th scope="col">
                            <button
                                onClick={() => handleSort('grade')}
                                className="btn btn-link p-0 text-decoration-none text-muted text-start small"
                            >
                                Sınıf
                                <ArrowUpDown size={14} className="ms-1"/>
                            </button>
                        </th>
                        <th scope="col">
                            <button
                                onClick={() => handleSort('branch')}
                                className="btn btn-link p-0 text-decoration-none text-muted text-start small"
                            >
                                Şube
                                <ArrowUpDown size={14} className="ms-1"/>
                            </button>
                        </th>
                        <th scope="col">
                            <button
                                onClick={() => handleSort('status')}
                                className="btn btn-link p-0 text-decoration-none text-muted text-start small"
                            >
                                Durum
                                <ArrowUpDown size={14} className="ms-1"/>
                            </button>
                        </th>
                        <th scope="col" className="text-nowrap">L</th>
                        <th scope="col" className="text-nowrap">S</th>
                        <th scope="col" className="text-nowrap">I</th>
                        <th scope="col" className="text-nowrap">V</th>
                        <th scope="col" className="text-nowrap">Puan</th>
                        <th scope="col" className="text-nowrap">Süre (dk)</th>
                        <th scope="col" className="text-nowrap">Soru</th>
                        <th scope="col" className="text-nowrap">Assignment ID</th>
                        <th scope="col" className="text-nowrap">Reset</th>
                    </tr>
                    </thead>
                    <tbody className="align-middle">
                    {paginatedData.map((item, index) => {
                        const status = getStatusLabel(item);
                        return (
                            <tr key={index}>
                                <td>
                                    {item.assignmentId && item.answeredQuestionCount > 0 ? (
                                        <Link
                                            to={`/applications/detail/${item.assignmentId}`}
                                            className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                                        >
                                            <FileText size={14}/>
                                            Detay
                                        </Link>
                                    ) : (
                                        <span className="text-muted">—</span>
                                    )}
                                </td>
                                <td>{(item.student?.studentStudentId ?? item.student?.studentId) ?? '—'}</td>
                                <td>
                                    {(item.student?.name || item.student?.lastName)
                                        ? [item.student?.name, item.student?.lastName].filter(Boolean).join(' ')
                                        : '—'}
                                </td>
                                <td>{item.student?.school ?? '—'}</td>
                                <td>{item.student?.grade ?? '—'}</td>
                                <td>{item.student?.branch ?? '—'}</td>
                                <td>
                                    <span className={`badge rounded-pill ${getStatusClasses(status)}`}>
                                        {status}
                                    </span>
                                </td>
                                <td className="text-end">{formatScore(item.listeningAndPronunciation)}</td>
                                <td className="text-end">{formatScore(item.textReadingAndSpeaking)}</td>
                                <td className="text-end">{formatScore(item.imageAnalysis)}</td>
                                <td className="text-end">{formatScore(item.videoAnalysis)}</td>
                                <td className="text-end fw-semibold">{formatScore(item.total)}</td>
                                <td className="text-end">{item.duration != null ? item.duration : '—'} / {item.examDuration != null ? item.examDuration : '—'}</td>
                                <td className="text-end">{item.answeredQuestionCount != null ? item.answeredQuestionCount : '—'} / {item.questionCount != null ? item.questionCount : '—'} </td>
                                <td>
                                    {item.assignmentId ? (
                                        <span
                                            className="d-inline-block text-truncate small"
                                            title={item.assignmentId}
                                        >
                                            {item.assignmentId}
                                        </span>
                                    ) : (
                                        <span className="text-muted">—</span>
                                    )}
                                </td>
                                <td>
                                    {item.answeredQuestionCount > 0 ? (
                                        <button
                                            type="button"
                                            onClick={() => handleResetStudentData(item.student?.studentId)}
                                            disabled={resettingStudentId === item.student?.studentId}
                                            className="btn btn-outline-warning btn-sm"
                                            title="Bu öğrencinin bu sınava ait cevap kayıtlarını sil"
                                        >
                                            <RotateCcw size={14} className="me-1"/>
                                            {resettingStudentId === item.student?.studentId ? '...' : 'Reset'}
                                        </button>
                                    ) : (
                                        <span className="text-muted">—</span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
                )}
            </div>

            {!loading && (
            <div className="mt-3 d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between gap-2">
                <div className="small text-muted">
                    Sayfa <span className="fw-semibold text-dark">{currentPage}</span> / {pageCount || 1}
                </div>
                <div className="d-inline-flex gap-2">
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="btn btn-outline-secondary btn-sm"
                    >
                        Önceki
                    </button>
                    <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, pageCount || 1))}
                        disabled={currentPage === pageCount || pageCount === 0}
                        className="btn btn-outline-secondary btn-sm"
                    >
                        Sonraki
                    </button>
                </div>
            </div>
            )}
        </div>
    );
};